import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme } from '@/contexts/ThemeContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Moon, Sun } from 'lucide-react'

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
})

type LoginFormData = z.infer<typeof loginSchema>

export const LoginPage = () => {
  const { t, i18n } = useTranslation()
  const { login } = useAuth()
  const { isDarkMode, toggleDarkMode } = useTheme()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true)
    try {
      // Use email as username since backend expects username field
      await login(data.email, data.password)
      toast.success(t('auth.loginSuccess', 'Login successful'))
      navigate('/orders')
    } catch (error: any) {
      // Enhanced error handling with specific messages
      let errorMessage = t('auth.loginFailed', 'Login failed')
      
      // Check for specific error scenarios
      if (error.message) {
        if (error.message.includes('401') || error.message.toLowerCase().includes('unauthorized')) {
          errorMessage = t('auth.invalidCredentials', 'Invalid email or password. Please check your credentials and try again.')
        } else if (error.message.includes('Network Error') || error.message.includes('ERR_NETWORK')) {
          errorMessage = t('auth.networkError', 'Unable to connect to server. Please check your internet connection.')
        } else if (error.message.includes('500')) {
          errorMessage = t('auth.serverError', 'Server error occurred. Please try again later.')
        } else if (error.message.includes('Bad credentials')) {
          errorMessage = t('auth.badCredentials', 'Invalid username or password')
        } else if (error.message.includes('User not found')) {
          errorMessage = t('auth.userNotFound', 'No account found with this email address')
        } else if (error.message.includes('Account locked') || error.message.includes('disabled')) {
          errorMessage = t('auth.accountLocked', 'Your account has been locked. Please contact support.')
        } else {
          // Use the error message from backend if it's specific
          errorMessage = error.message
        }
      }
      
      // Show error with appropriate styling
      toast.error(errorMessage, {
        duration: 5000,
      })
      
      // Clear password field on error for security
      form.setValue('password', '')
    } finally {
      setIsLoading(false)
    }
  }

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'uk' : 'en'
    i18n.changeLanguage(newLang)
  }

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-4">
            Warehouse Ukraine
          </h1>
          <div className="flex items-center justify-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleLanguage}
            >
              {t('auth.toggleLanguage')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleDarkMode}
            >
              {isDarkMode ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('auth.welcome')}</CardTitle>
             <CardDescription>
               {t('auth.loginSubtitle')}
             </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
                <div className="grid gap-3">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('auth.email')}</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder={t('auth.emailPlaceholder', 'm@example.com')}
                            autoComplete="email"
                            required
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid gap-3">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center">
                          <FormLabel>{t('auth.password')}</FormLabel>
                          <a
                            href="#"
                            className="ml-auto inline-block text-sm underline-offset-4 hover:underline"
                          >
                            Forgot your password?
                          </a>
                        </div>
                        <FormControl>
                          <Input
                            type="password"
                            autoComplete="current-password"
                            required
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="flex flex-col gap-3">
                  <Button type="submit" className="w-full" disabled={isLoading}>
                     {isLoading ? t('auth.loading') : t('auth.loginButton')}
                   </Button>
                </div>
              </form>
            </Form>
            
            <div className="mt-4 text-center text-sm">
              <p className="text-muted-foreground mb-2">Demo credentials:</p>
              <p className="text-xs text-muted-foreground">Email: admin@warehouse.com</p>
              <p className="text-xs text-muted-foreground">Password: admin123</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}